
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import {
  ArrowDownTrayIcon, BuildingOfficeIcon, ChevronDownIcon, ChevronUpIcon, HomeIcon, HomeModernIcon,
} from '@heroicons/react/24/solid';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import type { AsyncStatus } from '../../../../@types/AsyncStatus';
import type { RootState } from '../../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../../utils/JWTHeader';
import { ROUTES } from '../../../../utils/routes';
import tailwindConfig from '../../../../utils/tailwindColors';
import Badge from '../../../common/Badge';
import KPICard from '../../../common/KPICards';
import MUITable from '../../../common/MUITable';
import { showToast } from '../../../common/Notification';
import RedirectionCoproButtons from '../../../common/RedirectionCoproButtons';
import Spinner from '../../../common/Spinner';
import DismissibleModuleTutorialPopup from '../../../common/Tutorials/DismissibleModuleTutorialPopup';
import SCIMap from '../components/SCIsMap';

interface SCI {
  siret: string;
  siren: string;
  name: string | null;
  denominationUsuelleEtablissement: string | null;
  numeroVoieEtablissement: string;
  indiceRepetitionEtablissement: string | null;
  typeVoieEtablissement: string;
  libelleVoieEtablissement: string;
  codePostalEtablissement: string;
  libelleCommuneEtablissement: string;
  stats: {
    totalProperties: number;
    propertiesWithinCopro: number;
  };
  properties: {
    siren: string;
    denomination: string;
    nomCommune: string;
    numeroVoirie: string;
    indiceRepetitionVoirie: string;
    codeNatureVoie: string;
    libelleVoie: string;
    plotId: string;
    lettreBat: string | null;
    entreeEscalier: string | null;
    niveauLocal: number | null;
    numeroLocal: string | null;
    geometry: {
      type: string;
      coordinates: number[][][];
    };
  }[];
  owners: {
    lastname: string;
    firstnames: string[];
    birthdayDate?: string;
    role?: string;
  }[];
  ownersEntreprise: {
    name: string;
    siren: string | null;
    role: string;
  }[];
}

interface Copro {
  id: number;
  name: string;
  batchSize: number;
  type: number;
  validatedByUser: boolean;
  validatedByAdmin: boolean;
  remoteId: string;
  scis: {
    total: number;
    plots: {
      siren: string;
      plotId: string;
      geometry: {
        type: string;
        coordinates: number[][][][];
      };
    }[];
  };
}

const headers = [
  { title: 'SIRET' },
  { title: 'Dénomination' },
  { title: 'Adresse du siège' },
  { title: 'Nombre de biens dans la copro' },
  { title: 'Nombre de biens total' },
  { title: 'Propriétaires' },
  { title: 'Actions' },
];

const SCIs: React.FC = () => {
  const [expandedCopro, setExpandedCopro] = useState<number | null>(null);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copros, setCopros] = useState<Copro[]>([]);
  const [sciStats, setSciStats] = useState<SCI[]>([]);
  const [sciLoading, setSciLoading] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const location = useLocation();

  const fetchCopros = async() => {
    setStatus('PENDING');
    try {
      const coproResponse = await fetch(`/api/companies/${company.id}/copros/scis/stats`, JWTHeader(jwt));

      if (!coproResponse.ok) {
        throw new Error(`HTTP error! status: ${coproResponse.status}`);
      }

      const coprosData: Copro[] = await coproResponse.json();
      setCopros(coprosData);
      setStatus('SUCCESS');
    } catch (error) {
      console.error('Error fetching copros :', error);
      setStatus('ERROR');
      setErrorMessage('Erreur lors du chargement des copropriétés');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  };

  const fetchCoproDetails = async(coproId: number) => {
    setSciLoading(true);

    // Create a new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      const sciResponse = await fetch(`/api/copros/${coproId}/scis`, {
        headers: {
          Authorization: JWTAuthorization(jwt),
        },
        signal: abortControllerRef.current.signal, // Add the abort signal to the fetch request
      });

      if (!sciResponse.ok) {
        throw new Error(`HTTP error! status: ${sciResponse.status}`);
      }

      const sciStatsData: SCI[] = await sciResponse.json();
      setSciStats(sciStatsData);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('Fetch aborted');
      } else {
        console.error('Error fetching SCI details :', error);
        showToast('error', 'Une erreur est survenue lors du chargement des détails des SCIs.');
      }
    } finally {
      setSciLoading(false);
    }
  };

  const handleExpandCopro = (coproId: number) => {
    if (expandedCopro === coproId) {
      setExpandedCopro(null);
      setSciStats([]);
      // Cancel the ongoing fetch request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    } else {
      setExpandedCopro(coproId);
    }
  };

  const handleParcelClick = (parcelId: string) => {
    const relatedCopro = copros.find((copro) => copro?.scis?.plots?.some?.((plot) => String(plot?.plotId) === String(parcelId)));
    if (relatedCopro) {
      setExpandedCopro(relatedCopro.id);
    }
  };

  useEffect(() => {
    if (company && company.id && jwt) {
      fetchCopros();
    }
  }, [company, jwt]);

  useEffect(() => {
    // Check if coproId is passed in the location state
    const passedCoproId = location.state?.coproId;
    if (passedCoproId && copros.some((copro) => copro.id === passedCoproId)) {
      setExpandedCopro(passedCoproId);
    }
  }, [location.state, copros]);

  useEffect(() => {
    if (expandedCopro) {
      fetchCoproDetails(expandedCopro);
    }
  }, [expandedCopro]);

  const exportToExcel = async() => {
    if (sciStats.length === 0) {
      showToast('warning', 'Aucune donnée à exporter.');
      return;
    }

    const selectedCopro = copros.find((copro) => copro.id === expandedCopro);
    if (!selectedCopro) {
      showToast('error', 'Copropriété non trouvée.');
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('SCIs de la copropriété');

    // Styles
    const headerStyle: Partial<ExcelJS.Style> = {
      font: { bold: true, color: { argb: 'FFFFFFFF' } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
      border: {
        top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' },
      },
    };
    const subHeaderStyle: Partial<ExcelJS.Style> = {
      font: { bold: true },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } },
      alignment: { horizontal: 'left', vertical: 'middle' },
      border: {
        top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' },
      },
    };
    const normalCellStyle: Partial<ExcelJS.Style> = {
      alignment: { horizontal: 'left', vertical: 'middle' },
      border: {
        top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' },
      },
    };

    // Add copro info
    worksheet.addRow(['Informations Copropriété']).eachCell((cell) => Object.assign(cell, headerStyle));
    worksheet.addRow(['Nom', selectedCopro.name]).eachCell((cell, colNumber) => {
      Object.assign(cell, colNumber === 1 ? subHeaderStyle : normalCellStyle);
    });
    worksheet.addRow(['Nombre de SCIs', selectedCopro.scis.total.toString()]).eachCell((cell, colNumber) => {
      Object.assign(cell, colNumber === 1 ? subHeaderStyle : normalCellStyle);
    });
    worksheet.addRow(['Nombre de lots dans la copropriété', sciStats.reduce((sum, sci) => sum + sci.stats.propertiesWithinCopro, 0).toString()]).eachCell((cell, colNumber) => {
      Object.assign(cell, colNumber === 1 ? subHeaderStyle : normalCellStyle);
    });
    worksheet.addRow(['Nombre total de lots', sciStats.reduce((sum, sci) => sum + sci.stats.totalProperties, 0).toString()]).eachCell((cell, colNumber) => {
      Object.assign(cell, colNumber === 1 ? subHeaderStyle : normalCellStyle);
    });
    worksheet.addRow([]); // Empty row for spacing

    // Add SCI data
    const sciHeaders = ['SIRET', 'Nom de la SCI', 'Dénomination', 'Adresse du siège', 'Nombre de biens dans la copro', 'Nombre de biens total', 'Propriétaires'];
    worksheet.addRow(sciHeaders).eachCell((cell) => Object.assign(cell, headerStyle));
    sciStats.forEach((sci) => {
      worksheet.addRow([
        sci.siret,
        sci.name || `SCI ${sci.siren}`,
        sci.denominationUsuelleEtablissement || `SCI ${sci.siren}`,
        `${sci.numeroVoieEtablissement || ''}${sci.indiceRepetitionEtablissement || ''} ${sci.typeVoieEtablissement || ''} ${sci.libelleVoieEtablissement || ''} ${sci.codePostalEtablissement || ''} ${sci.libelleCommuneEtablissement || ''}`,
        sci.stats.propertiesWithinCopro.toString(),
        sci.stats.totalProperties.toString(),
        [
          sci.owners && sci.owners.length > 0 
            ? `Personnes physiques : ${sci.owners.map((owner) => `${owner.lastname} ${owner.firstnames && owner.firstnames.join(' ')} ${owner.role ? `(${owner.role})` : ''}`).join(' | ')}` 
            : '',
          sci.ownersEntreprise && sci.ownersEntreprise.length > 0 
            ? `Personnes morales : ${sci.ownersEntreprise.map((ownerCompany) => `${ownerCompany.name} ${ownerCompany.role ? `(${ownerCompany.role})` : ''}`).join(' | ')}` 
            : '',
        ].filter(Boolean).join('\r\n') || 'N/A',
      ]).eachCell((cell) => Object.assign(cell, normalCellStyle));
    });

    // Set column widths
    worksheet.columns.forEach((column, index) => {
      column.width = [20, 30, 40, 50, 25, 25, 50][index] || 15;
    });

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `SCIs_${selectedCopro.name}_${new Date().toISOString()}.xlsx`;
    link.click();
  };

  const renderSCITable = () => {
    const totalPropertiesInCopro = sciStats.reduce((sum, sci) => sum + sci.stats.propertiesWithinCopro, 0);
    const totalProperties = sciStats.reduce((sum, sci) => sum + sci.stats.totalProperties, 0);
    const copro = copros.find((coproSearched) => coproSearched.id === expandedCopro);

    return (
      <>
        <div className="mb-4">
          <div className="items-center justify-between md:flex">
            <h2 className="text-xl font-bold">Détails des SCIs dans la copropriété</h2>
            <button
              onClick={exportToExcel}
              className={`mt-2 flex w-full items-center justify-center rounded border border-rgpd-green px-4 py-2 text-rgpd-green transition-colors hover:bg-rgpd-green hover:text-white md:mt-0 md:w-auto ${sciLoading ? 'cursor-not-allowed opacity-50' : ''}`}
              disabled={sciLoading}
            >
              <ArrowDownTrayIcon className="mr-2 h-5 w-5" />
              Exporter
            </button>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <KPICard
              icon={<BuildingOfficeIcon className="h-6 w-6" />}
              title="Nombre de SCIs"
              value={!sciLoading && sciStats.length.toString()}
              loading={sciLoading}
            />
            <KPICard
              icon={<HomeIcon className="h-6 w-6" />}
              title="Biens possédés dans la copropriété"
              value={!sciLoading && totalPropertiesInCopro.toString()}
              loading={sciLoading}
            />
            <KPICard
              icon={<HomeModernIcon className="h-6 w-6" />}
              title="Biens total possédés par ces SCIs"
              value={!sciLoading && totalProperties.toString()}
              loading={sciLoading}
            />
          </div>
        </div>
        {sciLoading && (
          <div className="w-full">
            <MUITable
              data={[]}
              headers={headers}
              tableClass="min-w-full bg-white border text-sm"
              isSearchable
              disablePagination
            />
            <Skeleton
              count={copro.scis.total && copro.scis.total > 0 ? copro.scis.total : 2}
              height={45}
              className="mt-2 w-full"
              highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`}
            />
          </div>
        )}
        {!sciLoading && (
          <MUITable
            data={sciStats.map((sci) => ({
              SIRET: sci.siret,
              Dénomination: sci.name || sci.denominationUsuelleEtablissement || `SCI ${sci.siren}`,
              'Adresse du siège': `${sci.numeroVoieEtablissement || ''}${sci.indiceRepetitionEtablissement || ''} ${sci.typeVoieEtablissement || ''} ${sci.libelleVoieEtablissement || ''} ${sci.codePostalEtablissement || ''} ${sci.libelleCommuneEtablissement || ''}`,
              'Nombre de biens dans la copro': sci.stats.propertiesWithinCopro,
              'Nombre de biens total': sci.stats.totalProperties,
              Propriétaires: (
                <div className="whitespace-normal">
                  {sci.owners && sci.owners.length > 0 && (
                    <div className="mb-2">
                      <span className="font-semibold">Personnes physiques : </span><br/>
                      {sci.owners.map((owner, idx) => (
                        <React.Fragment key={idx}>
                          {idx > 0 && <br />}
                          · {`${owner.lastname} ${owner.firstnames && owner.firstnames.length > 0 ? owner.firstnames.join(' ') : ''} ${owner.role ? `(${owner.role})` : ''}`}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                  {sci.ownersEntreprise && sci.ownersEntreprise.length > 0 && (
                    <div>
                      <span className="font-semibold">Personnes morales : </span><br/>
                      {sci.ownersEntreprise.map((ownerCompany, idx) => (
                        <React.Fragment key={idx}>
                          {idx > 0 && <br />}
                          · {`${ownerCompany.name} ${ownerCompany.role ? `(${ownerCompany.role})` : ''}`}
                          <button className='ml-1 text-xs text-rgpd-green' onClick={() => window.open(`https://www.pappers.fr/entreprise/${ownerCompany.siren}`, '_blank', 'noopener,noreferrer')}>
                            <ArrowTopRightOnSquareIcon className="inline-block h-3 w-3" /> Pappers
                          </button>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                  {(!sci.owners || sci.owners.length === 0) && (!sci.ownersEntreprise || sci.ownersEntreprise.length === 0) && 'N/A'}
                </div>
              ),
              Actions: (
                <div className="flex flex-col space-y-2">
                  <Link
                    to={ROUTES.MONITORING_SCIS_SIREN.replace(':siren', sci.siren.toString())}
                    className="rounded bg-rgpd-blue-dark px-2 py-1 text-center text-white"
                  >
                    Voir les biens
                  </Link>
                  <button
                    className="rounded border border-rgpd-blue-dark px-2 py-1 text-rgpd-blue-dark hover:bg-rgpd-blue-dark hover:text-white"
                    onClick={() => window.open(`https://www.pappers.fr/entreprise/${sci.siren}`, '_blank', 'noopener,noreferrer')}
                  >
                    Voir sur Pappers
                  </button>
                </div>
              ),
            }))}
            headers={headers}
            tableClass="min-w-full bg-white border text-sm"
            isSearchable
            disablePagination
            sortableColumns={['SIRET', 'Dénomination', 'Nombre de biens dans la copro', 'Nombre de biens total']}
          />
        )}
        {sciStats.length === 0 && !sciLoading && (
          <div className="flex w-full place-content-center">
            <Badge
              mode="warning"
              text="Aucune SCI sur cette copropriété"
              className="my-4"
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex flex-col">
      <div className="mb-8 overflow-x-hidden rounded-lg bg-white p-4 shadow-md">
        <DismissibleModuleTutorialPopup tutorial="monitoring-scis-overall" />
        <div className="mt-4 mb-12 text-lg">
          <p>
            Cette page vous offre une vue d'ensemble sur les SCIs liées à vos copropriétés.
            <br />
            Vous pouvez voir les détails de chaque SCI en cliquant sur la copropriété correspondante dans le tableau ci-dessous.
          </p>
        </div>
        {status === 'SUCCESS' && (
          <div className="my-10">
            <div className="grid grid-cols-1 gap-10">
              <SCIMap
                copros={copros}
                onParcelClick={handleParcelClick}
                expandedCopro={expandedCopro}
                setExpandedCopro={setExpandedCopro}
              />
              <div className="flex flex-col justify-start">
                <MUITable
                  data={copros.filter((copro) => !expandedCopro || (expandedCopro && copro.id === expandedCopro)).map((copro) => ({
                    Nom: (
                      <div className="flex items-center">
                        <RedirectionCoproButtons coproId={copro.id} />
                        {copro.name}
                      </div>
                    ),
                    'Nombre de SCIs': copro.scis.total,
                    Actions: (
                      <div className="flex gap-2">
                        <button
                          className="hover:bg-rgpd-blue-dark-dark align-center mr-2 flex w-full items-center justify-center rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md"
                          onClick={() => handleExpandCopro(copro.id)}
                        >
                          {expandedCopro === copro.id ? (
                            <>
                              <ChevronUpIcon className="mr-2 h-5 w-5" />
                              {' '}
                              Revenir à la liste
                            </>
                          ) : (
                            <>
                              <ChevronDownIcon className="mr-2 h-5 w-5" />
                              {' '}
                              Voir le détail des SCIs
                            </>
                          )}
                        </button>
                      </div>
                    ),
                  }))}
                  headers={[
                    { title: 'Nom' },
                    { title: 'Nombre de SCIs' },
                    { title: 'Actions' },
                  ]}
                  tableClass="min-w-full bg-white border text-sm"
                  sortableColumns={['Nom', 'Nombre de SCIs']}
                  isSearchable={expandedCopro === null}
                  disablePagination={expandedCopro !== null}
                />
                {copros.map((copro) => (
                  <AnimatePresence key={copro.id}>
                    {expandedCopro === copro.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden rounded-b-[20px] border-x border-b px-4 pt-4 pb-4"
                      >
                        {renderSCITable()}
                      </motion.div>
                    )}
                  </AnimatePresence>
                ))}
              </div>
            </div>
          </div>
        )}
        {status === 'ERROR' && (
          <div className="my-10 text-center">
            <Badge mode="error" text={errorMessage} />
          </div>
        )}
        {status === 'PENDING' && (
          <div className="my-10 text-center">
            <Spinner size="xl" />
          </div>
        )}
      </div>
    </div>
  );
};

export default SCIs;
